import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import FormField from 'components/FormField';
import { useAPI } from 'contexts/APIProvider';
import { usePractice } from 'contexts/PracticeProvider';
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider';
import APIError from 'errors/APIError';
import PreferredNurseOption from 'logic/PreferredNurseOption';
import React from 'react'

interface AddPreferredNurseProps {
  open: boolean
  onClose: () => void
  reloadPreferredNurses: () => Promise<void>
}

const AddPreferredNurse: React.FC<AddPreferredNurseProps> = ({ open, onClose, reloadPreferredNurses }) => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const { practice } = usePractice()
  const [availableNurses, setAvailableNurses] = React.useState<PreferredNurseOption[]>([])
  const [selectedNurseId, setSelectedNurseId] = React.useState<number>()

  const fetchAvailableNurses = React.useCallback(async () => {
    if (!practice) return
    setAvailableNurses(await api.listPreferredNurseOptions({ practice_id: practice.practice_id }))
  }, [api, practice])

  const submit = React.useCallback(async () => {
    console.log('submit', { practice, selectedNurseId })
    if (!practice || !selectedNurseId) return
    try {
      await api.createPreferredNurse({
        practice_id: practice.practice_id,
        nurse_id: selectedNurseId,
      })
      await reloadPreferredNurses()
      showAlert('success', `The nurse is added to the preferred list`)
      onClose()
    } catch (e) {
      if (e instanceof APIError) {
        showAlert('error', e.message)
      }
      console.error(e)
    }
  }, [api, onClose, practice, reloadPreferredNurses, selectedNurseId, showAlert])


  const nurseSelectOptions = React.useMemo(() => {
    return availableNurses.map((nurse) => ({
      value: nurse.nurse_id,
      label: `${nurse.full_name} (${nurse.nurse_role})`,
    }))
  }, [availableNurses])

  React.useEffect(() => {
    fetchAvailableNurses()
  }, [fetchAvailableNurses])
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        Add Preferred Nurse
      </DialogTitle>
      <DialogContent>
        <FormField
          select
          sx={{ width: '100%' }}
          label='Select a nurse'
          value={selectedNurseId ?? ''}
          onChange={(e) => setSelectedNurseId(Number(e.target.value))}
        >
          {nurseSelectOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </FormField>
      </DialogContent>
      <DialogActions>
        <Button color='primary' variant='contained' onClick={submit}>
          Add to Preferred List
        </Button>
        <Button variant='outlined' onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddPreferredNurse